import { useState } from 'react';
import { ChevronDown, Store, Globe, CalendarCheck, ExternalLink, BookOpen, FileText } from 'lucide-react';
import { sanitizeCatalogueHtml } from '../lib/sanitizeHtml';

// `terms` and `redemption_instructions` are third-party HTML (Razorpay/Poshvine)
// injected into our page. The regex sanitiser that used to live here let four of
// nine standard payloads through; see src/lib/sanitizeHtml.js.
// Re-exported because the product page has always imported it from here.
export { sanitizeCatalogueHtml };

/** "['instore', 'online']" or ['instore'] → ['instore'] */
function parseChannels(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map((c) => String(c).toLowerCase().trim());
  return String(raw)
    .replace(/[[\]'"]/g, '')
    .split(',')
    .map((c) => c.toLowerCase().trim())
    .filter(Boolean);
}

const formatValidity = (epochOrDate) => {
  if (!epochOrDate) return null;
  const n = Number(epochOrDate);
  const d = Number.isFinite(n) && n > 1e9 ? new Date(n * 1000) : new Date(epochOrDate);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

function Section({ icon: Icon, title, html, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!html) return null;

  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-surface/60 transition-colors"
      >
        <Icon className="w-[18px] h-[18px] text-primary shrink-0" aria-hidden="true" />
        <span className="font-body font-semibold text-secondary flex-1">{title}</span>
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 pt-1 font-body text-sm text-muted-foreground leading-relaxed
            [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:pl-5 [&_ul]:pl-5 [&_li]:mb-1.5
            [&_p]:mb-3 [&_a]:text-primary [&_a]:underline [&_strong]:text-secondary"
          dangerouslySetInnerHTML={{ __html: sanitizeCatalogueHtml(html) }}
        />
      )}
    </div>
  );
}

/**
 * Everything the rewards catalogue knows about a gift card that the buyer needs:
 * where it can be redeemed, until when, how, and on what terms.
 */
export function GiftCardInfo({ product }) {
  const channels = parseChannels(product.redemption_channels);
  const validTill = formatValidity(product.valid_till);

  const hasAnything =
    channels.length || validTill || product.redemption_url ||
    product.redemption_instructions || product.terms;
  if (!hasAnything) return null;

  return (
    <div className="space-y-4">
      {(channels.length > 0 || validTill) && (
        <div className="flex flex-wrap gap-2">
          {channels.includes('online') && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1.5 font-body text-xs font-semibold">
              <Globe className="w-3.5 h-3.5" aria-hidden="true" />
              Redeem online
            </span>
          )}
          {channels.includes('instore') && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1.5 font-body text-xs font-semibold">
              <Store className="w-3.5 h-3.5" aria-hidden="true" />
              Redeem in store
            </span>
          )}
          {validTill && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-success/15 text-[#1c7a35] px-3 py-1.5 font-body text-xs font-semibold">
              <CalendarCheck className="w-3.5 h-3.5" aria-hidden="true" />
              Valid till {validTill}
            </span>
          )}
        </div>
      )}

      {product.redemption_url && (
        <a
          href={product.redemption_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 font-body text-sm font-medium text-primary hover:underline"
        >
          Where to redeem this card
          <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
        </a>
      )}

      <div className="space-y-3">
        <Section
          icon={BookOpen}
          title="How to redeem"
          html={product.redemption_instructions}
          defaultOpen
        />
        <Section icon={FileText} title="Terms & conditions" html={product.terms} />
      </div>
    </div>
  );
}
